import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Clock } from 'lucide-react';

export function HourlySalesChart({ data, viewMode }) {
  
  // Process hourly sales data
  const processHourlyData = (inputData) => {
    if (!inputData || !Array.isArray(inputData)) return [];

    // If data is from Table19 with SaleHour, TotalSales, TotalOrders
    if (inputData.length > 0 && inputData[0].SaleHour !== undefined) {
      // Group by hour across all days to get average/peak hours
      const hourlyMap = new Map();
      
      // Initialize hours 0-23
      for (let i = 0; i < 24; i++) {
        hourlyMap.set(i, {
          hour: i,
          displayHour: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i-12} PM`,
          amount: 0,
          orders: 0,
          count: 0
        });
      }

      // Aggregate data by hour
      inputData.forEach(item => {
        const hour = item.SaleHour;
        if (hour >= 0 && hour <= 23) {
          const existing = hourlyMap.get(hour);
          existing.amount += item.TotalSales || 0;
          existing.orders += item.TotalOrders || 0;
          existing.count += 1; // Number of days with data for this hour
        }
      });

      // Convert to array and calculate averages
      const processedData = Array.from(hourlyMap.values()).map(item => ({
        hour: item.displayHour,
        hourNum: item.hour,
        amount: item.amount,
        orders: item.orders,
        avgAmount: item.count > 0 ? item.amount / item.count : 0,
        avgOrders: item.count > 0 ? item.orders / item.count : 0
      }));

      return processedData;
    }

    // If data is already in the expected format
    if (inputData.length > 0 && inputData[0].hour !== undefined) {
      return inputData;
    }

    return [];
  };

  const processedData = processHourlyData(data);
  
  // Calculate statistics for the summary cards
  const calculateStats = (data) => {
    if (data.length === 0) {
      return {
        peakHour: 'N/A',
        peakSales: 0,
        avgPerHour: 0,
        totalHours: 0,
        totalSales: 0,
        totalOrders: 0
      };
    }

    // Find peak hour (hour with highest sales)
    let peakHour = data[0];
    data.forEach(item => {
      if (item.amount > peakHour.amount) {
        peakHour = item;
      }
    });

    // Calculate totals
    const totalSales = data.reduce((sum, item) => sum + item.amount, 0);
    const totalOrders = data.reduce((sum, item) => sum + item.orders, 0);
    
    // Count active hours (hours with sales > 0)
    const activeHours = data.filter(item => item.amount > 0).length;

    return {
      peakHour: peakHour.hour || 'N/A',
      peakSales: peakHour.amount || 0,
      avgPerHour: activeHours > 0 ? totalSales / activeHours : 0,
      totalHours: activeHours,
      totalSales,
      totalOrders
    };
  };

  const stats = calculateStats(processedData);

  // If no data after processing, show empty state
  if (processedData.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Hourly Sales Performance</h3>
            <p className="text-sm text-slate-500 mt-1">Sales trends throughout the {viewMode}</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-xl shadow-lg">
            <Clock className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="h-[350px] flex items-center justify-center">
          <div className="text-center">
            <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-400">No hourly sales data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Custom tooltip to show both sales and orders
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-800 mb-2">{data.hour}</p>
          <p className="text-sm text-slate-600">
            Sales: Rs. {(data.amount || 0).toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
          <p className="text-sm text-slate-600">
            Orders: {data.orders || 0}
          </p>
          {data.count > 1 && (
            <p className="text-xs text-slate-400 mt-1">
              Avg: Rs. {(data.avgAmount || 0).toFixed(0)} | {(data.avgOrders || 0).toFixed(1)} orders
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Hourly Sales Performance</h3>
          <p className="text-sm text-slate-500 mt-1">Sales trends throughout the {viewMode}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-xl shadow-lg">
          <Clock className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-gradient-to-br from-[#01796d]/10 to-teal-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Peak Hour</p>
          <p className="text-lg font-bold text-[#01796d]">{stats.peakHour}</p>
          <p className="text-xs text-slate-500 mt-1">
            Rs. {stats.peakSales.toLocaleString()}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Sales</p>
          <p className="text-lg font-bold text-purple-700">
            Rs. {stats.totalSales.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Avg/Active Hour</p>
          <p className="text-lg font-bold text-blue-700">
            Rs. {stats.avgPerHour.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Active Hours</p>
          <p className="text-lg font-bold text-orange-700">{stats.totalHours} hrs</p>
          <p className="text-xs text-slate-500 mt-1">{stats.totalOrders} total orders</p>
        </div>
      </div>

      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={processedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#01796d" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#01796d" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="hour" 
              tick={{ fill: '#64748b', fontSize: 11 }}
              axisLine={{ stroke: '#e2e8f0' }}
              interval={2} // Show every 2nd hour to avoid crowding
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: '#64748b', fontSize: 11 }}
              axisLine={{ stroke: '#e2e8f0' }}
              label={{ value: 'Sales (Rs)', angle: -90, position: 'insideLeft', fontSize: 10 }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#64748b', fontSize: 11 }}
              axisLine={{ stroke: '#e2e8f0' }}
              label={{ value: 'Orders', angle: 90, position: 'insideRight', fontSize: 10 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="amount" 
              stroke="#01796d" 
              strokeWidth={3}
              fill="url(#colorAmount)" 
              name="Sales Amount"
            />
            <Area 
              yAxisId="right"
              type="monotone" 
              dataKey="orders" 
              stroke="#8b5cf6" 
              strokeWidth={3}
              fill="url(#colorOrders)" 
              name="Orders"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Time period indicator */}
      <div className="mt-4 text-center">
        <p className="text-xs text-slate-400">
          Showing aggregated hourly data across all days in the selected range
        </p>
      </div>
    </div>
  );
}